import http from './api'

export type OutcomeSide = 'teamA' | 'draw' | 'teamB'
export type NewsScope =
  | 'upcoming'
  | 'past'
  | 'past_week'
  | 'past_month'
  | 'past_3months'
  | 'winners'

export function isPastNewsScope(scope: NewsScope): boolean {
  return (
    scope === 'past' ||
    scope === 'past_week' ||
    scope === 'past_month' ||
    scope === 'past_3months'
  )
}

export function isTimeNewsScope(scope: NewsScope): boolean {
  return scope === 'upcoming' || isPastNewsScope(scope)
}

export function pastWindowHoursForScope(scope: NewsScope): number {
  switch (scope) {
    case 'past_week':
      return 7 * 24
    case 'past_month':
      return 30 * 24
    case 'past_3months':
      return 90 * 24
    case 'past':
      return 60 * 24
    default:
      return 60 * 24
  }
}

export type NewsTournamentExtraKind =
  | 'top_scorer'
  | 'future_champion_start'
  | 'future_champion_second'
  | 'constructors_champion'

export interface NewsOutcomeSplit {
  label: string
  side: OutcomeSide
  votes: number
  pct: number
}

export interface NewsScorePrediction {
  score: string
  side: OutcomeSide
  votes: number
  pct: number
}

export interface NewsMatchCard {
  cardType: 'match'
  matchId: string
  league: string | null
  leagueLabel: string
  kickoffIso: string
  dateLabel: string
  timeLabel: string
  status: 'upcoming' | 'finished'
  teamA: { name: string }
  teamB: { name: string }
  totalPredictions: number
  winner: {
    teamA: NewsOutcomeSplit
    draw: NewsOutcomeSplit
    teamB: NewsOutcomeSplit
  }
  scorePredictions: NewsScorePrediction[]
  result: { score: string | null; winner: string | null } | null
  aiBrief?: string | null
}

export interface NewsTournamentPick {
  name: string
  votes: number
  pct: number
  team?: string | null
}

export interface NewsRaceGridSlot {
  position: number
  driver: string
  team?: string | null
  votes: number
  pct: number
}

export interface NewsRaceResultRow {
  position: number
  driver: string
  team?: string | null
}

export interface NewsTournamentExtraCard {
  cardType: 'tournament_extra'
  eventId: string
  kind: NewsTournamentExtraKind
  title: string
  league: string | null
  leagueLabel: string
  tournamentName: string | null
  deadlineIso: string
  dateLabel: string
  timeLabel: string
  status: 'upcoming' | 'finished'
  totalPredictions: number
  picks: NewsTournamentPick[]
  result: string | null
}

export type NewsFeedItem = NewsMatchCard | NewsTournamentExtraCard | NewsRaceCard

export interface NewsRaceCard {
  cardType: 'race'
  raceId: string
  day: number
  city: string
  country: string | null
  kickoffIso: string
  dateLabel: string
  timeLabel: string
  status: 'upcoming' | 'finished' | 'in-progress'
  totalPredictions: number
  p1Picks: NewsTournamentPick[]
  p2Picks?: NewsTournamentPick[]
  p3Picks?: NewsTournamentPick[]
  podiumPicks?: NewsTournamentPick[]
  constructorPicks?: NewsTournamentPick[]
  consensusGrid?: NewsRaceGridSlot[]
  result: NewsRaceResultRow[] | null
  liveResults: NewsRaceResultRow[] | null
  aiBrief?: string | null
}

export interface NewsFeedResponse {
  items: NewsFeedItem[]
  total: number
  limit: number
  offset: number
  hasMore: boolean
}

export function isNewsRaceCard(item: NewsFeedItem): item is NewsRaceCard {
  return item.cardType === 'race'
}

export function isNewsMatchCard(item: NewsFeedItem): item is NewsMatchCard {
  if (isNewsTournamentExtraCard(item) || isNewsRaceCard(item)) return false
  return item.cardType === 'match' || !('cardType' in item)
}

export function isNewsTournamentExtraCard(item: NewsFeedItem): item is NewsTournamentExtraCard {
  return item.cardType === 'tournament_extra'
}

/** Keep each Stats tab aligned with its scope even if the API returns mixed card types. */
export function filterNewsFeedByScope(items: NewsFeedItem[], scope: NewsScope): NewsFeedItem[] {
  if (scope === 'winners') return items.filter(isNewsTournamentExtraCard)

  const timed = items.filter(
    (item): item is NewsMatchCard | NewsRaceCard => isNewsMatchCard(item) || isNewsRaceCard(item),
  )
  const now = Date.now()

  if (scope === 'upcoming') {
    return timed.filter((item) => {
      const t = Date.parse(item.kickoffIso)
      return Number.isFinite(t) && t > now
    })
  }

  if (isPastNewsScope(scope)) {
    const start = now - pastWindowHoursForScope(scope) * 60 * 60 * 1000
    return timed.filter((item) => {
      const t = Date.parse(item.kickoffIso)
      return Number.isFinite(t) && t <= now && t >= start
    })
  }

  return timed
}

class NewsService {
  async getFootball(scope: NewsScope, limit = 5, offset = 0): Promise<NewsFeedResponse> {
    const params: Record<string, string | number> = { scope, limit, offset }
    if (isPastNewsScope(scope)) {
      // Compatibility: older backends only understand scope=past
      params.scope = 'past'
      params.pastHours = pastWindowHoursForScope(scope)
    }
    const res = await http.get<NewsFeedResponse>('/news/football', { params })
    return res.data
  }

  async getFormula1(scope: NewsScope, limit = 5, offset = 0): Promise<NewsFeedResponse> {
    const params: Record<string, string | number> = { scope, limit, offset }
    if (isPastNewsScope(scope)) {
      params.scope = 'past'
      params.pastHours = pastWindowHoursForScope(scope)
    }
    const res = await http.get<NewsFeedResponse>('/news/formula1', { params })
    return res.data
  }
}

const newsService = new NewsService()
export default newsService
